import * as THREE from 'three';
import {
  Kit, buildHouse, buildLHouse, buildChurch, buildBarn, buildSilo, buildWell,
  buildWindmillBody, buildWindmillRotor,
} from './buildings.js';
import {
  buildLamp, buildBench, buildStall, buildBarrel, buildCrate, buildHayBale, buildSignpost,
  addFence,
} from './props.js';
import { GeoBuilder, propMesh } from './geo.js';
import { baseHeightAt, slopeAt, MILL_HILL } from './terrain.js';
import {
  routeAt, ROUTE_LEN, PLAZA_R, VILLAGE_H, BRIDGE_SPAN, BRIDGE, POND, LAKE,
} from './layout.js';
import { routeY, ROAD_W, createTrack } from './roads.js';
import { TAU, rr, rnd, pick, chance, smoothstep, lerp, wrapAngle } from './util.js';

const cosSin = (a) => [Math.cos(a), Math.sin(a)];

/** local (lx,lz) -> world using position + yaw */
const toWorld = (x, z, ry, lx, lz) => [x + lx * Math.cos(ry) + lz * Math.sin(ry), z - lx * Math.sin(ry) + lz * Math.cos(ry)];

function footprintGround(x, z, w, d, ry) {
  let m = 1e9;
  for (const [sx, sz] of [[0, 0], [-1, -1], [1, -1], [-1, 1], [1, 1]]) {
    const [wx, wz] = toWorld(x, z, ry, (sx * w) / 2, (sz * d) / 2);
    m = Math.min(m, baseHeightAt(wx, wz));
  }
  return m;
}

export function createVillage(occ, spokes) {
  const group = new THREE.Group();
  group.name = 'village';
  const world = new Kit();
  const info = {
    chimneys: [], // {x,y,z}
    doors: [], // {x,z,yaw}
    plazaTrees: [],
    stops: [], // ice-cream van stops
    pens: [], // fenced pastures {x,z,w,d,ry,kind}
    fieldEdges: [],
    benches: [],
    lamps: [],
    mill: null,
    farm: null,
    houses: [],
    fountain: { x: 0, z: 0, y: VILLAGE_H },
    flowerBeds: [],
    tall: [],
  };

  const fenceB = new GeoBuilder();

  function place(kit, x, z, ry, y) {
    world.absorb(kit, x, y, z, ry);
  }

  function registerHouse(h, x, z, ry, y) {
    for (const c of h.chimneys || []) {
      const [wx, wz] = toWorld(x, z, ry, c.x, c.z);
      info.chimneys.push({ x: wx, y: y + c.y, z: wz });
    }
    if (h.doorLocal) {
      const [dx, dz] = toWorld(x, z, ry, h.doorLocal.x, h.doorLocal.z);
      info.doors.push({ x: dx, z: dz, yaw: ry, hx: x, hz: z });
    }
    info.houses.push({ x, z, ry, w: h.w, d: h.d });
    info.tall.push({ x, z, r: Math.hypot(h.w, h.d) * 0.5, h: (h.height || 6) + 0.5 });
  }

  // ------------------------------------------------------------ church
  {
    const ch = buildChurch();
    const front = -(PLAZA_R + 2.4);
    const cz = front - 10.15;
    const y = VILLAGE_H;
    place(ch.kit, 0, cz, 0, y);
    occ.rect(0, front - 8.5, 9.2, 17.6, 0, 1.6);
    info.tall.push({ x: 0, z: cz + 1.5, r: 6.5, h: 20 });
    info.church = { x: 0, z: cz, spire: { x: 0, y: y + ch.spire.y, z: cz + ch.spire.z } };
    info.doors.push({ x: 0, z: front + 0.9, yaw: 0, hx: 0, hz: cz });
    // small graveyard-ish garden + fence
    const fp = [];
    const fx0 = -8, fx1 = 8, fz0 = cz - 9.5, fz1 = cz + 8;
    // (kept open on purpose: cherry trees + flower beds instead of tombstones)
    info.flowerBeds.push({ x: -5.2, z: front + 1.3, r: 1.6 }, { x: 5.2, z: front + 1.3, r: 1.6 });
  }

  // ------------------------------------------------------------ plaza furniture
  {
    // benches on the rim, stalls, lamps, trees, in the wedges between spokes
    const spokeAngles = spokes.map((s) => s.angle).sort((a, b) => a - b);
    const mids = [];
    for (let i = 0; i < spokeAngles.length; i++) {
      const a = spokeAngles[i];
      const b = spokeAngles[(i + 1) % spokeAngles.length] + (i === spokeAngles.length - 1 ? TAU : 0);
      mids.push((a + b) / 2);
    }
    mids.forEach((a, i) => {
      // skip the wedge where the church sits (north)
      const [ca, sa] = cosSin(a);
      const isChurchSide = sa < -0.75;
      // trees just outside the curb
      info.plazaTrees.push({ x: ca * (PLAZA_R + 2.6), z: sa * (PLAZA_R + 2.6) });
      if (!isChurchSide) {
        // market stall
        const stall = buildStall(...pick([['#e94f4f', '#fff6e6'], ['#3d8bd9', '#fff6e6'], ['#f2a93b', '#fff6e6'], ['#4caf78', '#fff6e6']]));
        const sr = PLAZA_R - 2.6;
        const yaw = Math.atan2(-ca, -sa);
        place(stall, ca * sr, sa * sr, yaw, VILLAGE_H + 0.08);
        info.stallSpots = info.stallSpots || [];
        info.stallSpots.push({ x: ca * (sr - 2.3), z: sa * (sr - 2.3) });
      }
      // benches between stalls
      for (const off of [-0.22, 0.22]) {
        const ba = a + off;
        const [bc, bs] = cosSin(ba);
        if (isChurchSide) continue;
        const br = PLAZA_R - 0.9;
        place(buildBench(), bc * br, bs * br, Math.atan2(-bc, -bs), VILLAGE_H + 0.08);
        info.benches.push({ x: bc * (br - 0.9), z: bs * (br - 0.9) });
      }
    });
    // plaza lamps
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU + 0.2;
      const [ca, sa] = cosSin(a);
      const r = PLAZA_R - 0.4;
      const lamp = buildLamp();
      place(lamp, ca * r, sa * r, a + Math.PI / 2, VILLAGE_H + 0.08);
      info.lamps.push({ x: ca * r, z: sa * r });
    }

    // fountain
    const F = new Kit();
    F.body.cyl(3.55, 3.65, 0.7, 16, 0, 0, 0, '#cfc8b9', { jitter: 0.06 });
    F.body.geometry(new THREE.TorusGeometry(3.5, 0.24, 6, 20).rotateX(Math.PI / 2).translate(0, 0.78, 0), '#bdb5a5', { jitter: 0.05 });
    F.body.cyl(0.55, 0.75, 1.9, 8, 0, 0.5, 0, '#cfc8b9', { jitter: 0.05 });
    F.body.cyl(1.7, 0.6, 0.55, 12, 0, 1.75, 0, '#c1baa9');
    F.body.cyl(0.3, 0.42, 1.0, 8, 0, 2.2, 0, '#cfc8b9');
    F.body.sphere(0.42, 0, 3.4, 0, '#e8c14a');
    place(F, 0, 0, 0, VILLAGE_H + 0.08);
    info.fountain.y = VILLAGE_H + 0.08;

    // well near the houses
    const well = buildWell();
    const wa = 2.6;
    place(well.kit, Math.cos(wa) * 17, Math.sin(wa) * 17, wa, VILLAGE_H);
    occ.disc(Math.cos(wa) * 17, Math.sin(wa) * 17, 3.4, 1);
  }

  // ------------------------------------------------------------ core houses
  {
    let count = 0;
    for (let attempt = 0; attempt < 2600 && count < 26; attempt++) {
      const r = rr(13.6, 29.0), th = rnd() * TAU;
      const x = Math.cos(th) * r, z = Math.sin(th) * r;
      const kindRoll = rnd();
      let opts, w, d;
      if (kindRoll < 0.16) { w = 8.2; d = 7.4; opts = { kind: 'L' }; }
      else if (kindRoll < 0.42 && r < 24) { w = rr(4.8, 5.6); d = rr(4.8, 5.4); opts = { floors: 2, w, d, timber: chance(0.5), roofH: rr(2.0, 2.4) }; }
      else if (kindRoll < 0.52) { w = rr(8.2, 9.2); d = rr(5.0, 5.6); opts = { floors: 1, w, d, timber: chance(0.4) }; }
      else { w = rr(4.8, 6.4); d = rr(4.6, 5.6); opts = { floors: 1, w, d, timber: chance(0.25) }; }
      const yaw = Math.atan2(-x, -z) + rr(-0.18, 0.18);
      if (!occ.rectFree(x, z, w + 0.6, d + 0.6, yaw, 1.15)) continue;
      const y = footprintGround(x, z, w, d, yaw) - 0.02;
      const h = opts.kind === 'L' ? buildLHouse({}) : buildHouse(opts);
      place(h.kit, x, z, yaw, y);
      occ.rect(x, z, w + 0.6, d + 0.6, yaw, 0.7);
      registerHouse(h, x, z, yaw, y);
      // barrels / crates by some doors
      if (chance(0.45)) {
        const [bx, bz] = toWorld(x, z, yaw, (chance(0.5) ? 1 : -1) * (h.w / 2 - 0.5), h.d / 2 + 0.7);
        place(chance(0.5) ? buildBarrel() : buildCrate(), bx, bz, rr(0, TAU), y + 0.5);
      }
      count++;
    }
    info.coreCount = count;
  }

  // ------------------------------------------------------------ road-side houses along the ring
  {
    let count = 0;
    for (let s = 3; s < ROUTE_LEN - 3 && count < 16; s += rr(8, 12)) {
      if (s > BRIDGE_SPAN.s0 - 8 && s < BRIDGE_SPAN.s1 + 8) continue;
      const p = routeAt(s);
      const px = p.x, pz = p.z, lx = p.tz, lz = -p.tx;
      for (const side of [1, -1]) {
        if (chance(0.3)) continue;
        const w = rr(5.0, 6.6), d = rr(4.8, 5.8);
        const off = ROAD_W / 2 + 3.4 + d / 2 + rr(0, 1.8);
        const x = px + lx * side * off, z = pz + lz * side * off;
        const yaw = Math.atan2(-lx * side, -lz * side) + rr(-0.12, 0.12);
        if (!occ.rectFree(x, z, w + 0.6, d + 0.6, yaw, 1.6)) continue;
        if (slopeAt(x, z) > 0.14) continue;
        const y = footprintGround(x, z, w, d, yaw) - 0.02;
        const h = buildHouse({ floors: chance(0.25) ? 2 : 1, w, d, timber: chance(0.25) });
        place(h.kit, x, z, yaw, y);
        occ.rect(x, z, w + 0.6, d + 0.6, yaw, 0.9);
        registerHouse(h, x, z, yaw, y);
        count++;
      }
    }
    info.roadHouses = count;
  }

  // ------------------------------------------------------------ farm cluster
  {
    let done = false;
    const start = rr(0, ROUTE_LEN);
    for (let i = 0; i < 160 && !done; i++) {
      const s = (start + i * 2.3) % ROUTE_LEN;
      if (Math.abs(s - BRIDGE.s) < 40) continue;
      const p = routeAt(s);
      const rlen = Math.hypot(p.x, p.z);
      const ox = p.x / rlen, oz = p.z / rlen;
      const dist = ROAD_W / 2 + 19;
      const cx = p.x + ox * dist, cz = p.z + oz * dist;
      // cluster frame: front faces the road (toward centre)
      const yaw = Math.atan2(-ox, -oz);
      if (!occ.rectFree(cx, cz, 32, 30, yaw, 1) || slopeAt(cx, cz, 2) > 0.1) continue;
      // barn
      const barn = buildBarn();
      const y = footprintGround(cx, cz, 26, 26, yaw) - 0.02;
      place(barn.kit, cx, cz, yaw, y);
      info.tall.push({ x: cx, z: cz, r: 6, h: 8 });
      const silo = buildSilo();
      const [sx, sz] = toWorld(cx, cz, yaw, 8.2, -2.0);
      place(silo.kit, sx, sz, yaw, y);
      info.tall.push({ x: sx, z: sz, r: 2.4, h: 11 });
      const [fx, fz] = toWorld(cx, cz, yaw, -12.5, 2.0);
      const fh = buildHouse({ floors: 1, w: 6.4, d: 5.6, wall: '#f6e7c8', roof: '#b9483c' });
      place(fh.kit, fx, fz, yaw + Math.PI / 2 * 0 + 0.2, y);
      registerHouse(fh, fx, fz, yaw + 0.2, y);
      // hay bales
      for (let k = 0; k < 4; k++) {
        const [hx, hz] = toWorld(cx, cz, yaw, 5.5 + (k % 2) * 1.3, 5.5 + Math.floor(k / 2) * 0.9);
        place(buildHayBale(), hx, hz, yaw, y + 0.75);
      }
      // pen in front of the barn
      const [px, pz] = toWorld(cx, cz, yaw, 1, 10.5);
      const pw = 16, pd = 9;
      const pts = [];
      for (const [lx, lz] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) {
        const [wx, wz] = toWorld(px, pz, yaw, (lx * pw) / 2, (lz * pd) / 2);
        pts.push({ x: wx, z: wz, y: baseHeightAt(wx, wz) });
      }
      addFence(fenceB, pts, { closed: true });
      info.pens.push({ x: px, z: pz, w: pw - 1, d: pd - 1, ry: yaw, kind: 'cow' });
      occ.rect(cx, cz, 34, 32, yaw, 0.5);
      info.farm = { x: cx, z: cz, yaw, y, gate: { x: px, z: pz } };
      info.doors.push({ x: fx, z: fz, yaw, hx: fx, hz: fz });
      done = true;
      // dirt track to the road
      const near = routeAt(s);
      group.add(createTrack({ x: px + (near.x - px) * 0.0, z: pz }, { x: near.x, z: near.z }, 3.0, occ));
    }
  }

  // ------------------------------------------------------------ windmill
  {
    const mx = MILL_HILL.x, mz = MILL_HILL.z;
    const y = baseHeightAt(mx, mz) - 0.05;
    const yaw = Math.atan2(-mx, -mz);
    const body = buildWindmillBody();
    place(body.kit, mx, mz, yaw, y);
    const rotor = propMesh(buildWindmillRotor(), { flat: true });
    const holder = new THREE.Group();
    const [rx, rz] = toWorld(mx, mz, yaw, 0, body.hubZ);
    holder.position.set(rx, y + body.hubY, rz);
    holder.rotation.y = yaw;
    holder.add(rotor);
    group.add(holder);
    occ.disc(mx, mz, 12, 1);
    info.tall.push({ x: mx, z: mz, r: 3.4, h: 10 });
    info.mill = { x: mx, z: mz, y, rotor, yaw };
    info.doors.push({ x: toWorld(mx, mz, yaw, 0, 4.2)[0], z: toWorld(mx, mz, yaw, 0, 4.2)[1], yaw, hx: mx, hz: mz });
    // track down the hill to the ring road
    let best = 0, bd = 1e9;
    for (let s = 0; s < ROUTE_LEN; s += 2) {
      const p = routeAt(s);
      const d = Math.hypot(p.x - mx, p.z - mz);
      if (d < bd) { bd = d; best = s; }
    }
    const np = routeAt(best);
    const [tx, tz] = toWorld(mx, mz, yaw, 0, 5);
    group.add(createTrack({ x: tx, z: tz }, { x: np.x, z: np.z }, 2.6, occ));
  }

  // ------------------------------------------------------------ lamps along spokes & ring
  {
    const lampSpots = [];
    for (const sp of spokes) {
      const pts = sp.pts;
      for (let i = 4; i < pts.length - 2; i += 6) {
        const a = pts[i], b = pts[i + 1];
        const tx = b.x - a.x, tz = b.z - a.z;
        const l = Math.hypot(tx, tz) || 1;
        const side = (i / 6) % 2 ? 1 : -1;
        lampSpots.push({ x: a.x + (tz / l) * side * 2.5, z: a.z - (tx / l) * side * 2.5, y: a.y, ry: Math.atan2(tz / l * side, -tx / l * side) });
      }
    }
    for (let s = 6; s < ROUTE_LEN; s += 19) {
      if (s > BRIDGE_SPAN.s0 - 3 && s < BRIDGE_SPAN.s1 + 3) continue;
      const p = routeAt(s);
      const inner = 1; // toward the village
      const ln = Math.hypot(p.x, p.z);
      const dirx = -p.x / ln, dirz = -p.z / ln;
      const off = ROAD_W / 2 + 1.0;
      lampSpots.push({ x: p.x + dirx * off, z: p.z + dirz * off, y: routeY(s) - 0.05, ry: Math.atan2(-dirx, -dirz) });
    }
    // bridge lamps
    for (const sB of [BRIDGE_SPAN.s0 + 0.2, BRIDGE_SPAN.s1 - 0.2]) {
      const p = routeAt(sB);
      const ln = Math.hypot(p.x, p.z);
      const dirx = -p.x / ln, dirz = -p.z / ln;
      const off = ROAD_W / 2 + 0.5;
      lampSpots.push({ x: p.x + dirx * off, z: p.z + dirz * off, y: routeY(sB) + 1.35, ry: Math.atan2(-dirx, -dirz) });
    }
    for (const l of lampSpots) {
      const lamp = buildLamp();
      place(lamp, l.x, l.z, l.ry ?? 0, l.y);
      occ.disc(l.x, l.z, 0.9, 1);
      info.lamps.push(l);
    }
  }

  // ------------------------------------------------------------ signposts at spoke ends
  for (const sp of spokes) {
    const e = sp.pts[sp.pts.length - 1];
    const s = ROUTE_LEN * 0; // eslint-disable-line
    const ln = Math.hypot(e.x, e.z);
    const dx = -e.x / ln, dz = -e.z / ln;
    const x = e.x + dx * 4.0 + dz * 2.6, z = e.z + dz * 4.0 - dx * 2.6;
    place(buildSignpost(), x, z, Math.atan2(dx, dz), baseHeightAt(x, z));
    occ.disc(x, z, 0.8, 1);
  }

  // ------------------------------------------------------------ van stops: where spokes meet the ring
  {
    const stopSpokes = spokes.map((s) => s).filter((_, i) => i % 2 === 0 || i === 3);
    for (const sp of spokes) {
      const e = sp.pts[sp.pts.length - 1];
      // nearest route s
      let best = 0, bd = 1e9;
      for (let s = 0; s < ROUTE_LEN; s += 1) {
        const p = routeAt(s);
        const d = Math.hypot(p.x - e.x, p.z - e.z);
        if (d < bd) { bd = d; best = s; }
      }
      info.stops.push({ s: best, spokeAngle: sp.angle });
    }
    info.stops.sort((a, b) => a.s - b.s);
    // never stop on the bridge
    info.stops = info.stops.filter((st) => Math.abs(st.s - BRIDGE.s) > 20);
  }

  // ------------------------------------------------------------ meshes
  const built = world.buildAll();
  const body = propMesh(built.body);
  body.name = 'village-body';
  group.add(body);

  const litMat = new THREE.MeshBasicMaterial({ vertexColors: true, color: '#8ec6f0' });
  const darkMat = new THREE.MeshBasicMaterial({ vertexColors: true, color: '#8ec6f0' });
  const glowMat = new THREE.MeshBasicMaterial({ vertexColors: true, color: '#fff2c4' });
  if (built.lit) group.add(new THREE.Mesh(built.lit, litMat));
  if (built.dark) group.add(new THREE.Mesh(built.dark, darkMat));
  if (built.glow) group.add(new THREE.Mesh(built.glow, glowMat));

  const fb = fenceB.build();
  if (fb) group.add(propMesh(fb));

  // fountain water + spray
  const water = new THREE.Mesh(
    new THREE.CylinderGeometry(3.3, 3.3, 0.06, 20),
    new THREE.MeshStandardMaterial({ color: '#5fd3f2', roughness: 0.15, metalness: 0.0, emissive: '#1b6f9a', emissiveIntensity: 0.25 }),
  );
  water.position.set(0, VILLAGE_H + 0.08 + 0.71, 0);
  group.add(water);
  const DROPS = 44;
  const drops = new THREE.InstancedMesh(
    new THREE.SphereGeometry(0.1, 6, 5),
    new THREE.MeshStandardMaterial({ color: '#c6f0ff', roughness: 0.2, emissive: '#7fd0ff', emissiveIntensity: 0.4 }),
    DROPS,
  );
  drops.frustumCulled = false;
  group.add(drops);
  const dm = new THREE.Matrix4();
  const dq = new THREE.Quaternion();
  const dp = new THREE.Vector3();
  const ds = new THREE.Vector3();

  const day = { lit: new THREE.Color('#8ec6f0').multiplyScalar(0.92), dark: new THREE.Color('#84b8e2'), glow: new THREE.Color('#fff0c0').multiplyScalar(0.85) };
  const night = { lit: new THREE.Color(2.7, 1.9, 0.82), dark: new THREE.Color('#0f1834'), glow: new THREE.Color(3.4, 2.6, 1.2) };

  const api = {
    group,
    info,
    litMat, darkMat, glowMat,
    update(dt, time, sky) {
      const on = smoothstep(0.3, 0.75, sky.night);
      litMat.color.copy(day.lit).lerp(night.lit, on);
      darkMat.color.copy(day.dark).lerp(night.dark, on);
      glowMat.color.copy(day.glow).lerp(night.glow, on);
      // windmill
      if (info.mill) info.mill.rotor.rotation.z += dt * 0.55;
      // fountain spray
      const T = 1.3;
      const base = VILLAGE_H + 0.08;
      for (let i = 0; i < DROPS; i++) {
        const ph = ((time / T) + i / DROPS) % 1;
        const t = ph * T;
        const a = i * 2.39996;
        const vh = 0.8 + (i % 4) * 0.42;
        const vy = 4.4 + (i % 3) * 0.55;
        const y = 3.3 + vy * t - 4.9 * t * t;
        dp.set(Math.cos(a) * vh * t, base + y, Math.sin(a) * vh * t);
        ds.setScalar(y > 0.78 ? 1 : 0.0001);
        dm.compose(dp, dq, ds);
        drops.setMatrixAt(i, dm);
      }
      drops.instanceMatrix.needsUpdate = true;
    },
  };
  return api;
}
